import type { BaseTextAttributes } from '@blocksuite/store';
import { type ReadonlySignal, signal } from '@preact/signals-core';
import * as Y from 'yjs';

import type { InlineEditor } from '../inline-editor.js';
import { seededRandom } from '../utils/seeded-random.js';

export interface InlineRevealRange {
  from: Y.RelativePosition;
  to: Y.RelativePosition;
}

export const INLINE_REVEAL_MAX_MS = 4000;

/** Base character interval, with the pause following the previously shown ch. */
export function inlineRevealDelayMs(ch: string, random: () => number): number {
  const pause =
    ch === '\n'
      ? 400
      : '.!?'.includes(ch) && ch
        ? 250
        : ',;:'.includes(ch) && ch
          ? 120
          : 0;
  return 25 + random() * 35 + pause;
}

interface PendingReveal {
  range: InlineRevealRange;
  runId: string;
  timer: number;
  view: Window;
}

/** Hidden ranges follow Yjs indices; the underlying text is never changed. */
export class InlineRevealService<T extends BaseTextAttributes> {
  private readonly _hidden = signal<readonly InlineRevealRange[]>([]);
  readonly hidden$: ReadonlySignal<readonly InlineRevealRange[]> = this._hidden;
  private readonly _pending = new Set<PendingReveal>();

  constructor(private readonly editor: InlineEditor<T>) {}

  private _indices(
    range: InlineRevealRange
  ): { from: number; to: number } | null {
    const text = this.editor.yText;
    const doc = text.doc;
    if (!doc) return null;
    const from = Y.createAbsolutePositionFromRelativePosition(range.from, doc);
    const to = Y.createAbsolutePositionFromRelativePosition(range.to, doc);
    if (
      !from ||
      !to ||
      from.type !== text ||
      to.type !== text ||
      from.index >= to.index
    )
      return null;
    return { from: from.index, to: to.index };
  }

  hiddenIndexRanges(): { from: number; to: number }[] {
    const ranges = this._hidden
      .peek()
      .map(range => this._indices(range))
      .filter((range): range is { from: number; to: number } => range !== null)
      .sort((a, b) => a.from - b.from);
    const merged: { from: number; to: number }[] = [];
    for (const range of ranges) {
      const last = merged.at(-1);
      if (last && range.from <= last.to) last.to = Math.max(last.to, range.to);
      else merged.push({ ...range });
    }
    return merged;
  }

  private _publish(): void {
    this._hidden.value = [...this._pending].map(pending => pending.range);
  }

  private _remove(pending: PendingReveal): void {
    pending.view.clearTimeout(pending.timer);
    this._pending.delete(pending);
    this._publish();
  }

  reveal(input: {
    index: number;
    length: number;
    seed: string;
    runId: string;
    delayMs?: number;
  }): void {
    const view = this.editor.rootElement?.ownerDocument.defaultView;
    if (!view || view.matchMedia('(prefers-reduced-motion: reduce)').matches)
      return;
    const text = this.editor.yText;
    const from = Math.max(0, input.index);
    const to = Math.min(text.length, from + input.length);
    if (from >= to) return;
    const delay = Math.max(0, input.delayMs ?? 0);
    const deadline = view.performance.now() + delay + INLINE_REVEAL_MAX_MS;
    const random = seededRandom(input.seed);
    let previous = text.toString()[from - 1] ?? '';
    const pending: PendingReveal = {
      range: {
        from: Y.createRelativePositionFromTypeIndex(text, from),
        to: Y.createRelativePositionFromTypeIndex(text, to, -1),
      },
      runId: input.runId,
      timer: 0,
      view,
    };
    const tick = () => {
      if (!this._pending.has(pending)) return;
      if (
        view.matchMedia('(prefers-reduced-motion: reduce)').matches ||
        view.performance.now() >= deadline
      ) {
        this._remove(pending);
        return;
      }
      const indices = this._indices(pending.range);
      if (!indices) {
        this._remove(pending);
        return;
      }
      const character = String.fromCodePoint(
        text.toString().codePointAt(indices.from)!
      );
      previous = character;
      const next = indices.from + character.length;
      if (next >= indices.to) {
        this._remove(pending);
        return;
      }
      pending.range = {
        from: Y.createRelativePositionFromTypeIndex(text, next),
        to: pending.range.to,
      };
      this._publish();
      pending.timer = view.setTimeout(
        tick,
        Math.min(
          inlineRevealDelayMs(previous, random),
          deadline - view.performance.now()
        )
      );
    };
    this._pending.add(pending);
    this._publish();
    pending.timer = view.setTimeout(
      tick,
      delay +
        Math.min(inlineRevealDelayMs(previous, random), INLINE_REVEAL_MAX_MS)
    );
  }

  finish(runId?: string): void {
    let changed = false;
    for (const pending of this._pending) {
      if (runId !== undefined && pending.runId !== runId) continue;
      pending.view.clearTimeout(pending.timer);
      this._pending.delete(pending);
      changed = true;
    }
    if (changed) this._publish();
  }
}
